"use client"

import { useState } from "react"
import Image from "next/image"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Copy, Check, Download, RefreshCw } from "lucide-react"
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard"

type ImageService = "placehold" | "picsum" | "placeholder"

interface ServiceConfig {
  name: string
  description: string
  supportsText: boolean
  supportsColor: boolean
}

const services: Record<ImageService, ServiceConfig> = {
  placehold: {
    name: "Placehold.co",
    description: "Simple placeholder with custom colors and text",
    supportsText: true,
    supportsColor: true,
  },
  picsum: {
    name: "Lorem Picsum",
    description: "Random beautiful photos",
    supportsText: false,
    supportsColor: false,
  },
  placeholder: {
    name: "Placeholder.com",
    description: "Solid color placeholders",
    supportsText: true,
    supportsColor: true,
  },
}

const commonSizes = [
  { label: "Thumbnail", width: 150, height: 150 },
  { label: "Small", width: 300, height: 200 },
  { label: "Medium", width: 600, height: 400 },
  { label: "Large", width: 1200, height: 800 },
  { label: "Banner", width: 1200, height: 400 },
  { label: "Square", width: 500, height: 500 },
  { label: "Social (OG)", width: 1200, height: 630 },
  { label: "Instagram", width: 1080, height: 1080 },
  { label: "Twitter Header", width: 1500, height: 500 },
  { label: "YouTube Thumbnail", width: 1280, height: 720 },
]

const defaultColors = [
  { bg: "CCCCCC", fg: "969696", name: "Gray" },
  { bg: "3B82F6", fg: "FFFFFF", name: "Blue" },
  { bg: "10B981", fg: "FFFFFF", name: "Green" },
  { bg: "F59E0B", fg: "FFFFFF", name: "Orange" },
  { bg: "EF4444", fg: "FFFFFF", name: "Red" },
  { bg: "8B5CF6", fg: "FFFFFF", name: "Purple" },
  { bg: "000000", fg: "FFFFFF", name: "Black" },
  { bg: "FFFFFF", fg: "000000", name: "White" },
]

function generateUrl(
  service: ImageService,
  width: number,
  height: number,
  bgColor: string,
  fgColor: string,
  text: string
): string {
  switch (service) {
    case "placehold":
      let url = `https://placehold.co/${width}x${height}`
      if (bgColor || fgColor) {
        url += `/${bgColor || "CCCCCC"}/${fgColor || "969696"}`
      }
      if (text) {
        url += `?text=${encodeURIComponent(text)}`
      }
      return url

    case "picsum":
      return `https://picsum.photos/${width}/${height}`

    case "placeholder":
      let pUrl = `https://via.placeholder.com/${width}x${height}`
      if (bgColor) {
        pUrl += `/${bgColor}`
        if (fgColor) {
          pUrl += `/${fgColor}`
        }
      }
      if (text) {
        pUrl += `?text=${encodeURIComponent(text)}`
      }
      return pUrl

    default:
      return `https://placehold.co/${width}x${height}`
  }
}

export function PlaceholderImage() {
  const [service, setService] = useState<ImageService>("placehold")
  const [width, setWidth] = useState(600)
  const [height, setHeight] = useState(400)
  const [bgColor, setBgColor] = useState("CCCCCC")
  const [fgColor, setFgColor] = useState("969696")
  const [text, setText] = useState("")
  const { copy, isCopied } = useCopyToClipboard()
  const [refreshKey, setRefreshKey] = useState(0)

  const imageUrl = generateUrl(service, width, height, bgColor, fgColor, text)
  const serviceConfig = services[service]

  const copyHtml = () => {
    const html = `<img src="${imageUrl}" alt="${text || "Placeholder"}" width="${width}" height="${height}" />`
    copy(html, "html")
  }

  const copyMarkdown = () => {
    const md = `![${text || "Placeholder"}](${imageUrl})`
    copy(md, "markdown")
  }

  const applySize = (w: number, h: number) => {
    setWidth(w)
    setHeight(h)
  }

  const applyColor = (bg: string, fg: string) => {
    setBgColor(bg)
    setFgColor(fg)
  }

  const refresh = () => {
    setRefreshKey((k) => k + 1)
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Placeholder Image Generator</CardTitle>
          <CardDescription>
            Generate placeholder images for mockups and development
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Service Selection */}
          <div className="space-y-2">
            <Label>Image Service</Label>
            <Select value={service} onValueChange={(v) => setService(v as ImageService)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(services).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex flex-col">
                      <span>{config.name}</span>
                      <span className="text-xs text-muted-foreground">{config.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Dimensions */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="width">Width (px)</Label>
              <Input
                id="width"
                type="number"
                value={width}
                onChange={(e) => setWidth(parseInt(e.target.value) || 100)}
                min={1}
                max={4000}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="height">Height (px)</Label>
              <Input
                id="height"
                type="number"
                value={height}
                onChange={(e) => setHeight(parseInt(e.target.value) || 100)}
                min={1}
                max={4000}
              />
            </div>
          </div>

          {/* Common Sizes */}
          <div className="space-y-2">
            <Label>Quick Sizes</Label>
            <div className="flex flex-wrap gap-2">
              {commonSizes.map((size) => (
                <Button
                  key={size.label}
                  variant="outline"
                  size="sm"
                  onClick={() => applySize(size.width, size.height)}
                >
                  {size.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Colors */}
          {serviceConfig.supportsColor && (
            <div className="space-y-2">
              <Label>Colors</Label>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Background</Label>
                  <div className="flex gap-2">
                    <Input
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value.replace("#", ""))}
                      placeholder="CCCCCC"
                      className="font-mono"
                    />
                    <input
                      type="color"
                      value={`#${bgColor}`}
                      onChange={(e) => setBgColor(e.target.value.replace("#", ""))}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Text/Foreground</Label>
                  <div className="flex gap-2">
                    <Input
                      value={fgColor}
                      onChange={(e) => setFgColor(e.target.value.replace("#", ""))}
                      placeholder="969696"
                      className="font-mono"
                    />
                    <input
                      type="color"
                      value={`#${fgColor}`}
                      onChange={(e) => setFgColor(e.target.value.replace("#", ""))}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {defaultColors.map((color) => (
                  <Button
                    key={color.name}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => applyColor(color.bg, color.fg)}
                  >
                    <div
                      className="w-4 h-4 rounded border"
                      style={{ backgroundColor: `#${color.bg}` }}
                    />
                    {color.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Text */}
          {serviceConfig.supportsText && (
            <div className="space-y-2">
              <Label htmlFor="text">Custom Text</Label>
              <Input
                id="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={`${width}x${height}`}
              />
            </div>
          )}

          {/* Preview */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Preview</Label>
              {service === "picsum" && (
                <Button variant="ghost" size="sm" onClick={refresh}>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  New Image
                </Button>
              )}
            </div>
            <div className="border rounded-lg p-4 bg-muted/30 flex items-center justify-center overflow-hidden">
              <Image
                key={refreshKey}
                src={service === "picsum" ? `${imageUrl}?random=${refreshKey}` : imageUrl}
                alt={`${width} by ${height} placeholder preview`}
                width={Math.max(1, width)}
                height={Math.max(1, height)}
                unoptimized
                className="max-w-full max-h-[400px] object-contain"
                style={{ maxWidth: Math.min(width, 800), maxHeight: Math.min(height, 400) }}
              />
            </div>
          </div>

          {/* URLs and Copy Options */}
          <div className="space-y-2">
            <Label>Image URL</Label>
            <div className="flex gap-2">
              <Input value={imageUrl} readOnly className="font-mono text-sm" />
              <Button
                variant="outline"
                size="icon"
                onClick={() => copy(imageUrl, "url")}
              >
                {isCopied("url") ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Copy Options */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => copy(imageUrl, "url")}>
              <Copy className="h-4 w-4 mr-2" />
              Copy URL
            </Button>
            <Button variant="outline" onClick={copyHtml}>
              {isCopied("html") ? <Check className="h-4 w-4 mr-2 text-green-500" /> : null}
              Copy HTML
            </Button>
            <Button variant="outline" onClick={copyMarkdown}>
              {isCopied("markdown") ? <Check className="h-4 w-4 mr-2 text-green-500" /> : null}
              Copy Markdown
            </Button>
            <Button variant="outline" asChild>
              <a href={imageUrl} download={`placeholder-${width}x${height}.png`} target="_blank">
                <Download className="h-4 w-4 mr-2" />
                Download
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
